import React from "react";
import {Link} from "react-router-dom";
import EasGlobalStyles from "../components/eas/EasGlobalStyles";
import EasLandingNav from "../components/eas/EasLandingNav";
import EasHero from "../components/eas/EasHero";
import EasReveal from "../components/eas/EasReveal";

/**
 * EAS landing — assembled. Sections in order:
 *   nav · hero · trust strip · mission · what-we-see · system · manifesto ·
 *   numbers · testimonials · big-cta · footer
 *
 * Sections beyond hero/nav are inlined here for clarity. If this file gets
 * unwieldy split each <section> into its own EasXxxSection.tsx — they have no
 * cross-section state.
 */
export default function EasLanding(): JSX.Element {
    return (
        <main className="eas-root">
            <EasGlobalStyles/>
            <EasLandingNav/>
            <EasHero/>

            {/* trust strip */}
            <div style={{
                padding: "32px 0",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                borderBottom: "1px solid rgba(255,255,255,0.08)"
            }}>
                <div className="eas-container" style={stripRowStyle}>
                    <span>Trusted by 950+ teens</span><span>·</span>
                    <span>5 years of refinement</span><span>·</span>
                    <span>30‑day money‑back</span><span>·</span>
                    <span>Lifetime access</span>
                </div>
            </div>

            {/* mission */}
            <section id="mission" className="eas-section">
                <EasReveal>
                    <div className="eas-container eas-grid-two-col">
                        <div>
                            <p className="eas-section-eyebrow">Our mission</p>
                            <h2 className="eas-h2">A generation of teens who <em>run their own lives</em>.</h2>
                        </div>
                        <blockquote style={missionQuoteStyle}>
                            We help teenagers discover and use their full potential — to follow their own path and
                            pursue even the most ambitious plans.
                        </blockquote>
                    </div>
                </EasReveal>
            </section>

            {/* what we see */}
            <section className="eas-section" style={{paddingTop: 0}}>
                <EasReveal>
                    <div className="eas-container">
                        <p className="eas-section-eyebrow">What we see</p>
                        <h2 className="eas-h2">Three things every parent of a teenager <em>recognises</em>.</h2>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                            gap: 18,
                            marginTop: 48
                        }}>
                            {PROBLEMS.map((p) => (
                                <div key={p.num} style={problemCardStyle}>
                                    <span style={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: 11,
                                        letterSpacing: "0.18em",
                                        color: "#5ce08d"
                                    }}>{p.num}</span>
                                    <h3 style={{
                                        fontFamily: '"Fraunces", serif',
                                        fontWeight: 400,
                                        fontSize: 24,
                                        lineHeight: 1.15,
                                        margin: "16px 0 12px",
                                        color: "#fff",
                                        letterSpacing: "-0.01em"
                                    }}>{p.title}</h3>
                                    <p style={{
                                        fontSize: 15,
                                        color: "rgba(255,255,255,0.6)",
                                        margin: 0,
                                        lineHeight: 1.55
                                    }}>{p.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </EasReveal>
            </section>

            {/* the system */}
            <section id="system" className="eas-section">
                <EasReveal>
                    <div className="eas-container">
                        <div className="eas-pad-block" style={sysWrapStyle}>
                            <p className="eas-section-eyebrow">Effective Action System</p>
                            <h2 className="eas-h2" style={{marginBottom: 18}}>A six-week operating system for <em>ambitious
                                teenagers</em>.</h2>
                            <p style={{
                                fontSize: 18,
                                lineHeight: 1.6,
                                maxWidth: "60ch",
                                color: "rgba(255,255,255,0.7)",
                                margin: 0
                            }}>
                                Forty short videos. Real exercises with real feedback. A community of motivated peers
                                and weekly office hours with the founder. Refined with nearly a thousand teens across
                                Poland — now translated, recut, and rewritten for English-speaking families.
                            </p>
                            <div className="eas-grid-pillars">
                                {PILLARS.map((p, i) => (
                                    <div key={i} style={{background: "#131e18", padding: 32}}>
                                        <div style={{
                                            fontFamily: '"Fraunces", serif',
                                            fontSize: 64,
                                            fontWeight: 300,
                                            letterSpacing: "-0.03em",
                                            color: "#5ce08d",
                                            lineHeight: 1,
                                            marginBottom: 16
                                        }}>0{i + 1}</div>
                                        <h3 style={{
                                            fontFamily: '"Fraunces", serif',
                                            fontWeight: 500,
                                            fontSize: 22,
                                            margin: "0 0 10px",
                                            color: "#fff"
                                        }}>{p.title}</h3>
                                        <p style={{
                                            fontSize: 14,
                                            color: "rgba(255,255,255,0.6)",
                                            margin: 0,
                                            lineHeight: 1.6
                                        }}>{p.body}</p>
                                    </div>
                                ))}
                            </div>
                            <div style={{
                                marginTop: 48,
                                display: "flex",
                                gap: 16,
                                flexWrap: "wrap",
                                alignItems: "center"
                            }}>
                                <Link to="/system" className="eas-btn eas-btn--primary">See full program <span
                                    className="eas-arr">→</span></Link>
                                <span style={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 13,
                                    color: "rgba(255,255,255,0.5)",
                                    letterSpacing: "0.1em",
                                    textTransform: "uppercase"
                                }}>$2,997 · lifetime · 30‑day guarantee</span>
                            </div>
                        </div>
                    </div>
                </EasReveal>
            </section>

            {/* manifesto */}
            <EasManifesto/>

            {/* numbers */}
            <section className="eas-section">
                <EasReveal>
                    <div className="eas-container">
                        <p className="eas-section-eyebrow">By the numbers</p>
                        <h2 className="eas-h2">Five years. Five hundred families. <em>One stubborn idea.</em></h2>
                        <div className="eas-grid-numbers">
                            {NUMS.map((n, i) => (
                                <div key={i} style={{background: "#0b1410", padding: "36px 28px"}}>
                                    <div style={{
                                        fontFamily: '"Fraunces", serif',
                                        fontWeight: 300,
                                        fontSize: "clamp(40px, 5vw, 64px)",
                                        letterSpacing: "-0.03em",
                                        lineHeight: 1,
                                        color: "#fff"
                                    }}>
                                        {n.big}<sub style={{
                                        color: "#5ce08d",
                                        fontSize: "0.4em",
                                        verticalAlign: "super",
                                        marginLeft: 4
                                    }}>{n.unit}</sub>
                                    </div>
                                    <div style={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: 11,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.5)",
                                        marginTop: 12
                                    }}>{n.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </EasReveal>
            </section>

            {/* big cta */}
            <section className="eas-section" style={{paddingTop: 0}}>
                <EasReveal>
                    <div className="eas-container">
                        <div className="eas-pad-block-cta" style={bigCtaStyle}>
                            <div style={{
                                position: "absolute",
                                inset: 0,
                                background: "radial-gradient(circle at 30% 50%, rgba(42,209,103,0.3), transparent 60%)",
                                pointerEvents: "none"
                            }}/>
                            <div style={{position: "relative"}}>
                                <h2 style={{
                                    fontFamily: '"Fraunces", serif',
                                    fontWeight: 300,
                                    fontSize: "clamp(40px, 6vw, 80px)",
                                    lineHeight: 0.98,
                                    letterSpacing: "-0.025em",
                                    margin: "0 0 24px",
                                    color: "#fff"
                                }}>
                                    Give them the tools.<br/><em style={emStyle}>They'll do the rest.</em>
                                </h2>
                                <p style={{
                                    fontSize: 18,
                                    color: "rgba(255,255,255,0.85)",
                                    maxWidth: "50ch",
                                    margin: "0 auto 40px"
                                }}>
                                    Effective Action System is open for international students. Lifetime access.
                                    Thirty-day money-back guarantee.
                                </p>
                                <Link to="/system" className="eas-btn eas-btn--primary eas-btn--large">View the
                                    program <span className="eas-arr">→</span></Link>
                                <div style={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 12,
                                    color: "rgba(255,255,255,0.7)",
                                    marginTop: 24,
                                    letterSpacing: "0.1em",
                                    textTransform: "uppercase"
                                }}>
                                    <strong style={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 600,
                                        color: "#fff",
                                        fontSize: 15,
                                        letterSpacing: 0,
                                        textTransform: "none"
                                    }}>$2,997</strong> · lifetime · 30-day guarantee
                                </div>
                            </div>
                        </div>
                    </div>
                </EasReveal>
            </section>

            <EasFooter/>
        </main>
    );
}

/**
 * Long-form founder manifesto — sticky sidebar with portrait + meta on the
 * left, ~10 paragraphs of narrative on the right with two italic pull-quote
 * "lift" paragraphs. The narrative is the brand voice; if you edit copy here,
 * preserve the emotional arc (02:14 desk memory → "motivation is a skill" →
 * five years of refinement → direct address to parents → invitation).
 */
function EasManifesto(): JSX.Element {
    return (
        <section id="founder" style={manifestoWrapStyle}>
            <div style={manifestoBgStyle}/>
            <div style={manifestoDotsStyle}/>
            <EasReveal>
                <div className="eas-container">
                    <div className="eas-grid-manifesto">
                        <aside className="eas-manifesto-side" style={manifestoSideStyle}>
                            <div style={founderPhotoStyle}>
                                <img
                                    src="https://produkacja.s3.eu-central-1.amazonaws.com/web/o-nas/md-2025-05.png"
                                    alt="Mikołaj Dyblik — founder of Effective Action System"
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover"
                                    }}
                                    loading="lazy"
                                />
                                <div style={{
                                    position: "absolute",
                                    inset: 0,
                                    background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.6))"
                                }}/>
                                <div style={{
                                    position: "absolute",
                                    bottom: 24,
                                    left: 24,
                                    right: 24,
                                    zIndex: 2,
                                    color: "#fff"
                                }}>
                                    <strong style={{
                                        fontFamily: '"Fraunces", serif',
                                        fontStyle: "italic",
                                        fontSize: 24,
                                        fontWeight: 400,
                                        display: "block"
                                    }}>Mikołaj</strong>
                                    <span style={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: 11,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.6)"
                                    }}>Founder, Effective Action System</span>
                                </div>
                            </div>
                            <div style={metaBlockStyle}>
                                <strong style={{
                                    color: "rgba(255,255,255,0.85)",
                                    fontWeight: 500,
                                    display: "block",
                                    marginBottom: 4
                                }}>A note from the founder</strong>
                                Filed: 23:47<br/>
                                <span>Wroclaw → wherever you are</span>
                            </div>
                            <div style={{width: 48, height: 1, background: "#5ce08d", margin: "24px 0"}}/>
                            <div style={{
                                ...metaBlockStyle,
                                textTransform: "none",
                                letterSpacing: "0.04em",
                                fontSize: 12,
                                lineHeight: 1.7,
                                color: "rgba(255,255,255,0.5)"
                            }}>
                                Read time<br/>
                                <span style={{color: "rgba(255,255,255,0.85)"}}>~4 minutes.</span><br/>
                                Worth every one.
                            </div>
                        </aside>

                        <div>
                            <p className="eas-section-eyebrow" style={{marginBottom: 32}}>Manifesto · 01</p>
                            <h2 style={manifestoTitleStyle}>Into the <em
                                style={{fontStyle: "italic", color: "#8eecaf", fontWeight: 400}}>night</em>.</h2>

                            <p style={manifestoLedeStyle}>
                                <span style={{
                                    display: "block",
                                    fontSize: "1.6em",
                                    lineHeight: 0.5,
                                    color: "#5ce08d",
                                    marginBottom: 24,
                                    fontStyle: "normal"
                                }}>“</span>
                                The moment a teenager stops believing they're capable is almost always quiet. No one
                                announces it. They just close a notebook a little earlier each week, and one day they
                                don't open it at all.
                            </p>

                            <div style={manifestoBodyStyle}>
                                <p>I remember the night I almost gave up on myself. I was sixteen, sitting at my desk
                                    past midnight, surrounded by half-finished plans and a stack of books I'd promised
                                    myself I'd read. The clock said 02:14. The page in front of me was empty. And I felt
                                    the very specific kind of tired that has nothing to do with sleep.</p>

                                <p>It wasn't that I didn't want things. I wanted everything. I wanted to build
                                    something, to be someone my future self wouldn't be embarrassed by, to stop watching
                                    other kids my age move while I stalled. <strong
                                        style={{color: "#fff", fontWeight: 500}}>What I didn't have was a
                                        system.</strong> I didn't know how to translate "I want to be different" into
                                    Tuesday at 4pm.</p>

                                <p style={liftStyle}>No one had ever taught me that motivation is a skill, not a
                                    feeling. That discipline isn't gritted teeth — it's a structure quiet enough that
                                    you forget you're using it.</p>

                                <p>So I started, badly. I read the wrong books. I copied other people's routines and
                                    quit them in a week. I confused looking productive for being productive. I burned
                                    out twice before I was nineteen. Each time I came back, I kept what worked and threw
                                    out what didn't, and slowly — over five years and nearly a thousand teenagers I
                                    worked with along the way — a system emerged.</p>

                                <p>It became <strong style={{color: "#fff", fontWeight: 500}}>Effective Action
                                    System</strong>. Six weeks. Forty short videos. A way of thinking that turns "I
                                    should" into "I did," without the parental pressure or the productivity-bro
                                    performance. It works because it was built backwards from the version of me that
                                    almost gave up at sixteen.</p>

                                <p style={liftStyle}>I built EAS so the next teenager sitting at their desk at 02:14 has
                                    something I didn't: a map.</p>

                                <p>This program isn't about pushing your child harder. They're already pushing
                                    themselves harder than you can see — most of it just doesn't reach the surface
                                    because they don't have anywhere to put it. EAS gives that energy a shape. A weekly
                                    review. A way to plan a Tuesday. A community of teenagers who are also, quietly,
                                    trying.</p>

                                <p>If you're a parent reading this, I want to say one thing directly: <strong
                                    style={{color: "#fff", fontWeight: 500}}>your teenager is not
                                    broken.</strong> They're under-equipped. There's a difference, and the difference is
                                    everything. Give them the tools and they will surprise you. They surprised me 950
                                    times before I sent this page live.</p>

                                <p>That's the whole pitch. No countdown timers, no fake scarcity, no "transform your
                                    child" theater. Just a system that took five years to build, refined with real
                                    teenagers, and now translated for families like yours.</p>

                                <p>If it's a fit — for your kid, for your family, right now — I'll meet them on the
                                    other side.</p>
                            </div>

                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 20,
                                marginTop: 56,
                                paddingTop: 32,
                                borderTop: "1px solid rgba(255,255,255,0.08)",
                                flexWrap: "wrap"
                            }}>
                                <div style={{
                                    fontFamily: '"Fraunces", serif',
                                    fontStyle: "italic",
                                    fontSize: 28,
                                    color: "#fff",
                                    lineHeight: 1
                                }}>Mikołaj
                                </div>
                                <div style={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 11,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.5)",
                                    lineHeight: 1.6
                                }}>
                                    Founder, Effective Action System<br/>
                                    <span style={{color: "rgba(255,255,255,0.7)"}}>Wroclaw, Poland</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </EasReveal>
        </section>
    );
}

function EasFooter(): JSX.Element {
    return (
        <footer id="contact" style={{
            background: "#060a08",
            color: "rgba(255,255,255,0.6)",
            padding: "80px 0 40px",
            borderTop: "1px solid rgba(255,255,255,0.08)"
        }}>
            <div className="eas-container">
                <div className="eas-grid-footer">
                    <div>
                        <h3 style={{
                            fontFamily: '"Fraunces", serif',
                            fontWeight: 400,
                            fontSize: 24,
                            color: "#fff",
                            margin: "0 0 12px",
                            letterSpacing: "-0.01em"
                        }}>Effective Action System</h3>
                        <p style={{fontSize: 14, lineHeight: 1.6, maxWidth: "36ch", margin: 0}}>We
                            help teenagers run their own lives — calmly, ambitiously, without burning out.</p>
                    </div>
                    <FooterCol title="Product"
                               links={[["/system", "Effective Action System"], ["/#system", "What's included"], ["/#founder", "Manifesto"]]}/>
                    <FooterCol title="Contact"
                               links={[["mailto:support@effectiveactionsystem.com", "support@effectiveactionsystem.com"]]}/>
                    <FooterCol title="Legal"
                               links={[["/legal/privacy", "Privacy policy"], ["/legal/terms", "Terms of service"]]}/>
                </div>
                <div style={{
                    paddingTop: 28,
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 16,
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 11,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.3)"
                }}>
                    <span>© {new Date().getFullYear()} Effective Action System — operated by PRODUKACJA Sp. z o.o.</span>
                    <span>Wroclaw → London → New York</span>
                </div>
            </div>
        </footer>
    );
}

const FooterCol = ({title, links}: { title: string; links: [string, string][] }): JSX.Element => (
    <div>
        <h4 style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.4)",
            margin: "0 0 16px"
        }}>{title}</h4>
        <ul style={{listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10}}>
            {links.map(([href, label]) => (
                <li key={href}><a href={href} style={{
                    color: "rgba(255,255,255,0.7)",
                    textDecoration: "none",
                    fontSize: 14
                }}>{label}</a></li>
            ))}
        </ul>
    </div>
);

const PROBLEMS = [
    {
        num: "01",
        title: "The friction at home",
        body: "\"Have you done your homework?\" — fifteen times a week. The relationship suffers and the homework still doesn't get done."
    },
    {
        num: "02",
        title: "The procrastination loop",
        body: "They mean to start. They want to do well. By the time they actually do, it's the night before the exam."
    },
    {
        num: "03",
        title: "The disappearing direction",
        body: "By 16 they've stopped talking about what they want. They're just trying to keep up with what's already on the calendar."
    },
];
const PILLARS = [
    {
        title: "Self-direction",
        body: "Help them name what they actually want — separate from what teachers, peers, and parents project onto them."
    },
    {
        title: "Daily systems",
        body: "The boring infrastructure that makes ambition survivable: planning, focus blocks, energy, recovery."
    },
    {
        title: "Resilient motivation",
        body: "How to keep going when motivation runs out — without burning the relationship with themselves."
    },
];
const NUMS = [
    {big: "950", unit: "+", label: "Teens through the program"},
    {big: "40", unit: "+", label: "Video lessons in EAS"},
    {big: "4.93", unit: "/5", label: "Average rating from 182 testimonials"},
    {big: "5", unit: "yr", label: "Refining the system"},
];

const stripRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 32,
    flexWrap: "wrap",
    opacity: 0.5,
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.6)"
};
const missionQuoteStyle: React.CSSProperties = {
    fontFamily: '"Fraunces", serif',
    fontWeight: 300,
    fontStyle: "italic",
    fontSize: "clamp(28px, 3.4vw, 42px)",
    lineHeight: 1.25,
    color: "rgba(255,255,255,0.95)",
    margin: 0,
    padding: "0 0 0 32px",
    borderLeft: "2px solid #2ad167"
};
const problemCardStyle: React.CSSProperties = {
    background: "#131e18",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 22,
    padding: 32
};
const sysWrapStyle: React.CSSProperties = {
    background: "linear-gradient(180deg, #131e18, #1c2a23)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 32,
    padding: "80px 64px"
};
const bigCtaStyle: React.CSSProperties = {
    background: "linear-gradient(160deg, #20b557, #0b1410)",
    borderRadius: 32,
    padding: "100px 64px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden"
};
const emStyle: React.CSSProperties = {fontStyle: "italic", color: "#5ce08d", fontWeight: 400};

const manifestoWrapStyle: React.CSSProperties = {position: "relative", padding: "120px 0", isolation: "isolate"};
const manifestoBgStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    zIndex: -1,
    background: "radial-gradient(ellipse 80% 60% at 20% 0%, rgba(72,122,76,0.18), transparent 60%), radial-gradient(ellipse 60% 40% at 90% 100%, rgba(72,122,76,0.12), transparent 60%), linear-gradient(180deg, #0a0e0b 0%, #050706 100%)"
};
const manifestoDotsStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    zIndex: -1,
    opacity: 0.4,
    backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
    backgroundSize: "3px 3px"
};
const manifestoSideStyle: React.CSSProperties = {position: "sticky", top: 120};
const founderPhotoStyle: React.CSSProperties = {
    aspectRatio: "4/5",
    background: "linear-gradient(140deg, #2ad167, #0b1410)",
    borderRadius: 32,
    position: "relative",
    overflow: "hidden",
    marginBottom: 24
};
const metaBlockStyle: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)",
    marginBottom: 24,
    lineHeight: 1.8
};
const manifestoTitleStyle: React.CSSProperties = {
    fontFamily: '"Fraunces", serif',
    fontWeight: 300,
    fontSize: "clamp(40px, 5.4vw, 76px)",
    lineHeight: 1.02,
    letterSpacing: "-0.025em",
    color: "#fff",
    margin: "0 0 48px",
    maxWidth: "16ch"
};
const manifestoLedeStyle: React.CSSProperties = {
    fontFamily: '"Fraunces", serif',
    fontStyle: "italic",
    fontWeight: 300,
    fontSize: "clamp(22px, 2.4vw, 30px)",
    lineHeight: 1.4,
    color: "rgba(255,255,255,0.92)",
    margin: "0 0 56px",
    maxWidth: "32ch",
    letterSpacing: "-0.005em"
};
const manifestoBodyStyle: React.CSSProperties = {
    fontSize: 17,
    lineHeight: 1.75,
    color: "rgba(255,255,255,0.78)",
    maxWidth: "58ch"
};
const liftStyle: React.CSSProperties = {
    fontFamily: '"Fraunces", serif',
    fontStyle: "italic",
    fontWeight: 300,
    fontSize: "clamp(22px, 2.2vw, 28px)",
    lineHeight: 1.45,
    color: "#fff",
    padding: "16px 0 16px 32px",
    borderLeft: "1px solid #2ad167",
    margin: "40px 0",
    maxWidth: "36ch",
    letterSpacing: "-0.005em"
};
